import React, { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import { collection, getDocs } from 'firebase/firestore';
import './RacesPage.css';

interface Race {
  id: string;
  name: string;
  date: any;
  location?: string;
  distance?: string;
  imageURL?: string;
  featured?: boolean;
}

type FilterId = 'this-week' | 'this-month' | 'marathon' | 'half' | '10k' | '5k' | 'ultra';

const FILTERS: { id: FilterId; label: string }[] = [
  { id: 'this-week',  label: 'This Week' },
  { id: 'this-month', label: 'This Month' },
  { id: 'marathon',   label: 'Marathons' },
  { id: 'half',       label: 'Half Marathons' },
  { id: '10k',        label: '10Ks' },
  { id: '5k',         label: '5Ks' },
  { id: 'ultra',      label: 'Ultras' },
];

const formatDate = (timestamp: any): string => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const getRaceDate = (race: Race): Date => {
  return race.date.toDate ? race.date.toDate() : new Date(race.date);
};

const matchesText = (race: Race, query: string): boolean => {
  const q = query.toLowerCase();
  return (
    race.name.toLowerCase().includes(q) ||
    (race.location ?? '').toLowerCase().includes(q)
  );
};

const matchesFilter = (race: Race, filter: FilterId): boolean => {
  const today = new Date();
  const raceDate = getRaceDate(race);
  const haystack = `${race.name} ${race.distance ?? ''}`.toLowerCase();

  switch (filter) {
    case 'this-week': {
      const weekEnd = new Date(today);
      weekEnd.setDate(today.getDate() + 7);
      return raceDate >= today && raceDate <= weekEnd;
    }
    case 'this-month': {
      const monthEnd = new Date(today);
      monthEnd.setDate(today.getDate() + 30);
      return raceDate >= today && raceDate <= monthEnd;
    }
    case 'marathon':
      return (
        haystack.includes('marathon') ||
        haystack.includes('26.2') ||
        haystack.includes('42k') ||
        haystack.includes('42.2')
      ) && !haystack.includes('half');
    case 'half':
      return (
        haystack.includes('half') ||
        haystack.includes('13.1') ||
        haystack.includes('21k') ||
        haystack.includes('21.1')
      );
    case '10k':
      return (
        haystack.includes('10k') ||
        haystack.includes('10 k') ||
        haystack.includes('10km') ||
        haystack.includes('10 km')
      );
    case '5k':
      return (
        haystack.includes('5k') ||
        haystack.includes('5 k') ||
        haystack.includes('5km') ||
        haystack.includes('5 km')
      );
    case 'ultra':
      return (
        haystack.includes('ultra') ||
        haystack.includes('50k') ||
        haystack.includes('50m') ||
        haystack.includes('100k') ||
        haystack.includes('100m')
      );
    default:
      return true;
  }
};

const applyFilters = (races: Race[], query: string, filter: FilterId | null): Race[] => {
  let result = races;
  if (query.trim()) result = result.filter(r => matchesText(r, query));
  if (filter) result = result.filter(r => matchesFilter(r, filter));
  return result;
};

const RaceCard: React.FC<{ race: Race; featured?: boolean }> = ({ race, featured }) => {
  const [imgError, setImgError] = useState(false);

  return (
    <div className={`race-card${featured ? ' race-card--featured' : ''}`}>
      <div className="race-card-image">
        {race.imageURL && !imgError ? (
          <img
            src={race.imageURL}
            alt={race.name}
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="race-card-image-fallback" />
        )}
      </div>
      <div className="race-card-body">
        <h2 className="race-card-name">{race.name}</h2>
        {race.location && <p className="race-card-location">{race.location}</p>}
        {race.distance && <p className="race-card-distance">{race.distance}</p>}
        <p className="race-card-date">{formatDate(race.date)}</p>
      </div>
    </div>
  );
};

const RacesPage: React.FC = () => {
  const [featured, setFeatured] = useState<Race[]>([]);
  const [upcoming, setUpcoming] = useState<Race[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterId | null>(null);

  useEffect(() => {
    const fetchRaces = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'races'));
        const today = new Date();

        const allUpcoming = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as Race))
          .filter(r => {
            if ((r as any).testRace) return false;
            if ((r as any).createdBy) return false; // exclude user-created private runs
            if (!r.date) return false;
            const raceDate = getRaceDate(r);
            return raceDate > today;
          })
          .sort((a, b) => getRaceDate(a).getTime() - getRaceDate(b).getTime());

        setFeatured(allUpcoming.filter(r => r.featured));
        setUpcoming(allUpcoming.filter(r => !r.featured));
      } catch (error) {
        console.error('Error fetching races:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRaces();
  }, []);

  const toggleFilter = (id: FilterId) => {
    setActiveFilter(prev => (prev === id ? null : id));
  };

  const isFiltering = search.trim().length > 0 || activeFilter !== null;
  const filteredFeatured = applyFilters(featured, search, activeFilter);
  const filteredUpcoming = applyFilters(upcoming, search, activeFilter);
  const hasAny = featured.length > 0 || upcoming.length > 0;
  const allFiltered = [...filteredFeatured, ...filteredUpcoming]
    .sort((a, b) => getRaceDate(a).getTime() - getRaceDate(b).getTime());

  return (
    <div className="races-page">
      <div className="races-hero">
        <h1 className="races-hero-rally">Rally</h1>
        <p className="races-hero-tagline">Voice Notes on Race Day</p>
      </div>

      {!loading && hasAny && (
        <>
          <div className="races-search-wrapper">
            <div className="races-search-inner">
              <span className="races-search-icon">🔍</span>
              <input
                className="races-search"
                type="text"
                placeholder="Search races"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="races-filters-wrapper">
            <div className="races-filters">
              {FILTERS.map(f => (
                <button
                  key={f.id}
                  className={`races-filter-chip${activeFilter === f.id ? ' races-filter-chip--active' : ''}`}
                  onClick={() => toggleFilter(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {loading && (
        <div className="races-loading">
          <div className="races-spinner" />
        </div>
      )}

      {!loading && !hasAny && (
        <div className="races-empty">
          <p>No upcoming races at the moment.</p>
        </div>
      )}

      {/* Filtered results — flat list */}
      {!loading && isFiltering && (
        <>
          {allFiltered.length > 0 ? (
            <section className="races-section">
              <div className="races-grid">
                {allFiltered.map(race => (
                  <RaceCard key={race.id} race={race} featured={race.featured} />
                ))}
              </div>
            </section>
          ) : (
            <div className="races-empty">
              <p>No races found.</p>
            </div>
          )}
        </>
      )}

      {/* Default view — featured + upcoming sections */}
      {!loading && !isFiltering && hasAny && (
        <>
          {featured.length > 0 && (
            <section className="races-section">
              <h2 className="races-section-title">Featured</h2>
              <div className="races-grid races-grid--featured">
                {featured.map(race => (
                  <RaceCard key={race.id} race={race} featured />
                ))}
              </div>
            </section>
          )}

          {upcoming.length > 0 && (
            <section className="races-section">
              <h2 className="races-section-title">
                {featured.length > 0 ? 'All Upcoming Races' : 'Upcoming Races'}
              </h2>
              <div className="races-grid">
                {upcoming.map(race => (
                  <RaceCard key={race.id} race={race} />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default RacesPage;
